import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import styled from '@emotion/styled';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSearchParams } from 'react-router-dom';
import { LoadingIndicator } from 'ts/common/components';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import DatePicker from 'ts/common/components/date-picker/DatePicker';
import { normalizeBrandLocaleTag } from 'ts/common/components/date-picker/brandCalendarDateFormat';
import { ButtonProps } from 'ts/common/components/gallery';
import { useBreakpoint, useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import { ExpiredBookingSessionModal } from 'client_react/booking/ExpiredBookingSessionModal';
import MobileDateAndTimePage from 'client_react/booking/MobileDateAndTimePage';
import BookingSessionHeading from 'client_react/booking/components/BookingSessionHeading';
import useBookingFlowSteps from 'client_react/booking/hooks/useBookingFlowSteps';
import { formatDateToString, getFirstAvailableDate } from 'client_react/helpers';
import ReCaptchaDisclaimer from './components/ReCaptchaDisclaimer';
import TimePicker from './components/TimePicker';
import { formatTimezone } from './helpers';
import { isBookingSessionError } from './useBookingSession';

const Container = styled(Box)`
    .grecaptcha-badge {
        visibility: hidden;
    }
`;

type Props = { recaptchaKey?: string };

const DateAndTimePage = ({ recaptchaKey }: Props) => {
    const {
        bookingSession,
        bookingSessionType,
        bookingSessionTypeAvailability,
        createBookingSession,
        isPending,
        updateBookingSession
    } = useClientBookingExperience();

    const { goToNextStep } = useBookingFlowSteps();

    const [searchParams] = useSearchParams();
    const expired = searchParams.has('expired') && !!searchParams.get('expired');

    const t = useTranslate('booking.dateAndTimePage');
    const isLargeScreen = useBreakpoint();
    const today = new Date(
        new Intl.DateTimeFormat('en-US', { timeZone: bookingSessionType?.timezone }).format(
            new Date()
        )
    ); //must pass in timezone to get the correct date

    const errorToast = useToast({
        status: 'error'
    });

    const { brandCustomDomain: customDomain = null } = bookingSessionType || {};

    const [selectedTimeslot, setSelectedTimeslot] = useState<string | undefined>();
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const formattedDate = selectedDate ? formatDateToString(selectedDate) : null;

    const useRecaptcha = !customDomain;

    // recaptchaKey is empty when the studio is exempt from reCAPTCHA (admin
    // override or fraud-score-based). In that case there is no widget to render
    // or execute, so continuing must fall through to submitting directly instead
    // of triggering a challenge that never resolves.
    const recaptchaActive = useRecaptcha && Boolean(recaptchaKey);

    const availableTimeslots =
        (formattedDate &&
            bookingSessionTypeAvailability &&
            bookingSessionTypeAvailability[formattedDate]) ||
        [];

    const isAvailabilityEmpty =
        !bookingSessionTypeAvailability ||
        (Object.keys(bookingSessionTypeAvailability).length === 1 &&
            'status' in bookingSessionTypeAvailability);

    const handleDateChange = (date: Date | null) => {
        if (!date) {
            return;
        }

        setSelectedDate(date);
        setSelectedTimeslot(undefined);
    };

    const handleTimeslotChange = (timeslot: string) => {
        setSelectedTimeslot(timeslot);
    };

    const firstAvailableDate = useMemo(
        () =>
            bookingSessionTypeAvailability
                ? getFirstAvailableDate(bookingSessionTypeAvailability)
                : null,
        [bookingSessionTypeAvailability]
    );

    const handleSubmit = useCallback(
        async (token?: string) => {
            if (!bookingSessionType) {
                return;
            }

            if (bookingSession) {
                const response = await updateBookingSession({
                    sessionTypePublicId: bookingSessionType.publicId,
                    startTime: selectedTimeslot
                });

                if (!response || isBookingSessionError(response)) {
                    errorToast({
                        title: t('apiError')
                    });
                } else {
                    goToNextStep();
                }
            } else {
                const response = await createBookingSession({
                    sessionTypePublicId: bookingSessionType.publicId,
                    startTime: selectedTimeslot,
                    recaptchaToken: token
                });

                if (!response || isBookingSessionError(response)) {
                    errorToast({
                        title: t('apiError')
                    });
                } else {
                    goToNextStep();
                }
            }
        },
        [
            bookingSession,
            bookingSessionType,
            createBookingSession,
            errorToast,
            goToNextStep,
            selectedTimeslot,
            t,
            updateBookingSession
        ]
    );

    const handleCaptchaChange = useCallback(
        (token: string | null) => {
            setRecaptchaToken(token);

            if (token) {
                handleSubmit(token);
            }
        },
        [handleSubmit]
    );

    const triggerReCaptchaValidation = useCallback(() => {
        if (!recaptchaKey || recaptchaToken) {
            return;
        }

        recaptchaRef.current?.execute();
    }, [recaptchaKey, recaptchaToken]);

    const handleContinueClick = useCallback(() => {
        if (recaptchaActive) {
            triggerReCaptchaValidation();
        } else {
            handleSubmit();
        }
    }, [handleSubmit, triggerReCaptchaValidation, recaptchaActive]);

    useEffect(() => {
        if (firstAvailableDate) {
            handleDateChange(
                new Date(
                    new Intl.DateTimeFormat('en-US', {
                        timeZone: bookingSessionType?.timezone
                    }).format(new Date(`${firstAvailableDate}T00:00:00`))
                )
            );
        }
    }, [firstAvailableDate, bookingSessionType?.timezone]);

    const buttons = useMemo<ButtonProps[]>(
        () => [
            {
                text: t('continue'),
                isDisabled: !selectedTimeslot || (recaptchaActive && !recaptchaRef.current),
                onClick: handleContinueClick,
                isLoading: isPending
            }
        ],
        [t, selectedTimeslot, recaptchaActive, handleContinueClick, isPending]
    );

    if (!bookingSessionType) {
        return null;
    }

    return (
        <Container>
            <BookingSessionHeading
                bookingSession={bookingSession}
                bookingSessionType={bookingSessionType}
            />
            <ExpiredBookingSessionModal expired={expired} />
            {isLargeScreen ? (
                <>
                    <Flex direction="column" gap="24px">
                        <Text
                            as="div"
                            dangerouslySetInnerHTML={{
                                __html: bookingSessionType.description || ''
                            }}
                        />
                        {!bookingSessionTypeAvailability ? (
                            <Flex justify="center" align="center" minH="400px">
                                <LoadingIndicator />
                            </Flex>
                        ) : (
                            <Flex flexDirection="row" gap="20px" flexWrap="nowrap" width="100%">
                                <Flex alignItems="flex-start" flexWrap="wrap">
                                    {bookingSessionTypeAvailability && (
                                        <DatePicker
                                            inline
                                            centeredDate
                                            disableUnavailableDays
                                            onChange={handleDateChange}
                                            min={today}
                                            value={selectedDate ? selectedDate : null}
                                            availabilities={bookingSessionTypeAvailability}
                                            brandTimezone={bookingSessionType.timezone}
                                            brandLocale={normalizeBrandLocaleTag(
                                                bookingSessionType.locale
                                            )}
                                        />
                                    )}
                                </Flex>
                                <Flex direction="column">
                                    {selectedDate && (
                                        <Flex
                                            alignItems="flex-start"
                                            direction="column"
                                            paddingTop="4px"
                                        >
                                            <Text
                                                fontSize="16px"
                                                fontWeight={600}
                                                fontFamily="open-sans, sans-serif"
                                            >
                                                {new Date(selectedDate).toLocaleDateString([], {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })}
                                            </Text>
                                            <Text
                                                fontSize="12px"
                                                fontFamily="open-sans, sans-serif"
                                                fontWeight={400}
                                            >
                                                {formatTimezone(bookingSessionType?.timezone)}
                                            </Text>
                                        </Flex>
                                    )}

                                    <Box
                                        flexWrap="wrap"
                                        height="400px"
                                        width="100%"
                                        overflowY="auto"
                                        sx={{
                                            '&::-webkit-scrollbar': {
                                                display: 'none'
                                            },
                                            msOverflowStyle: 'none', // For IE and Edge
                                            scrollbarWidth: 'none' // For Firefox
                                        }}
                                    >
                                        {!isAvailabilityEmpty ? (
                                            <TimePicker
                                                selectedTimeslot={selectedTimeslot}
                                                onTimeslotChange={handleTimeslotChange}
                                                timeslots={availableTimeslots}
                                                brandTimezone={bookingSessionType.timezone}
                                            />
                                        ) : (
                                            <Text
                                                paddingTop="10px"
                                                fontSize="16px"
                                                fontFamily="open-sans, sans-serif"
                                            >
                                                {t('noAvailabilities')}
                                            </Text>
                                        )}
                                    </Box>
                                </Flex>
                            </Flex>
                        )}
                        <ResponsiveButtonGroup buttons={buttons} />
                    </Flex>

                    {recaptchaActive && <ReCaptchaDisclaimer />}
                </>
            ) : (
                <MobileDateAndTimePage
                    availabilities={bookingSessionTypeAvailability || {}}
                    bookingSessionType={bookingSessionType}
                    hideDatePicker={!!isAvailabilityEmpty}
                    onSubmit={handleContinueClick}
                    selectedDate={selectedDate}
                    selectedTimeslot={selectedTimeslot}
                    onDateChange={handleDateChange}
                    onTimeslotChange={handleTimeslotChange}
                    timeslots={availableTimeslots}
                    hasAvailabilitiesLoaded={bookingSessionTypeAvailability !== null}
                />
            )}
            {recaptchaActive && (
                <ReCAPTCHA
                    data-testid="booking-recaptcha-element"
                    onChange={handleCaptchaChange}
                    ref={recaptchaRef}
                    sitekey={recaptchaKey}
                    size={'invisible'}
                />
            )}
        </Container>
    );
};

export default DateAndTimePage;
